import { useEffect, useState } from 'react'
import {
  describeLastUsed,
  INGEST_URL,
  issueIngestKey,
  loadIngestKeys,
  type IngestKey,
} from '@core/ingest'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * Setting up "Share → Naar Familie" on a phone.
 *
 * This exists because of a platform limitation, not a product idea: iOS will
 * not let a web app be a Share Sheet target, so a bill that arrives as a PDF
 * in Mail cannot get in. A Shortcut can be a Share Sheet target, and this
 * screen hands it the two things it needs.
 *
 * The instructions are written for someone who has never made a Shortcut,
 * because that is who will be following them.
 */
export function ShareSetting() {
  const [keys, setKeys] = useState<IngestKey[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    loadIngestKeys().then(setKeys).catch(() => setKeys([]))
  }, [])

  async function create() {
    setBusy(true)
    setError('')
    try {
      await issueIngestKey('iPhone')
      setKeys(await loadIngestKeys())
    } catch {
      setError('Aanmaken lukte niet.')
    }
    setBusy(false)
  }

  async function copy(what: string, label: string) {
    try {
      await navigator.clipboard.writeText(what)
      setCopied(label)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      setError('Kopiëren lukte niet. Houd de tekst ingedrukt om te kopiëren.')
    }
  }

  const live = keys?.[0]

  return (
    <Card className="mb-4">
      <p className="font-medium mb-1">Delen vanuit Mail</p>
      <p className="text-sm text-muted mb-3">
        Een PDF die je per e-mail krijgt, kan je met één tik naar Familie sturen.
        Daarvoor zet je eenmalig een Snelkoppeling op je iPhone.
      </p>

      <ErrorNote>{error}</ErrorNote>

      {keys === null ? (
        <p className="text-sm text-muted">Even geduld…</p>
      ) : !live ? (
        <Button onClick={create} disabled={busy}>
          {busy ? 'Bezig…' : 'Instellen'}
        </Button>
      ) : (
        <>
          <ol className="text-sm space-y-2 mb-3 list-decimal list-inside text-muted">
            <li>Open de app <span className="text-ink">Opdrachten</span> op je iPhone.</li>
            <li>Maak een nieuwe opdracht en zet <span className="text-ink">Delen</span> aan bij
              de instellingen, zodat ze in het deelmenu verschijnt.</li>
            <li>Voeg de actie <span className="text-ink">Inhoud ophalen van URL</span> toe.</li>
            <li>Zet de methode op <span className="text-ink">POST</span>, het type op{' '}
              <span className="text-ink">Formulier</span>, en voeg een veld{' '}
              <span className="text-ink">file</span> toe met de gedeelde invoer.</li>
            <li>Voeg een koptekst toe met naam <span className="text-ink">x-ingest-key</span>.</li>
            <li>Noem de opdracht <span className="text-ink">Naar Familie</span>.</li>
          </ol>

          <div className="space-y-2">
            <div>
              <p className="text-xs text-muted mb-1">URL</p>
              <button
                onClick={() => copy(INGEST_URL, 'url')}
                className="w-full text-left text-xs break-all bg-surface border border-line rounded-lg p-2"
              >
                {INGEST_URL}
              </button>
            </div>
            <div>
              <p className="text-xs text-muted mb-1">Waarde voor x-ingest-key</p>
              <button
                onClick={() => copy(live.key, 'key')}
                className="w-full text-left text-xs break-all bg-surface border border-line rounded-lg p-2"
              >
                {live.key}
              </button>
            </div>
            {copied && <p className="text-xs text-ok">Gekopieerd.</p>}
          </div>

          <p className="text-xs text-muted mt-3">
            {describeLastUsed(live.lastUsedAt)}. Deze sleutel kan alleen documenten
            versturen — er is niets mee te lezen.
          </p>

          <div className="mt-3">
            <Button tone="quiet" onClick={create} disabled={busy}>
              Nieuwe sleutel maken
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
